import { useCallback, useEffect, useState } from 'react';

import { expVal } from '@confluence/feature-experiments';
import { cfetch, BadStatusError } from '@confluence/network';

const REMOTE_OBJECT_SUGGESTION_COUNT_OPERATION = 'RemoteObjectSuggestionCount';

const REMOTE_OBJECT_SUGGESTION_COUNT_QUERY = `
query RemoteObjectSuggestionCount($cloudId: ID!, $spaceKey: String!) {
	confluence_remoteObjectSuggestionCount(cloudId: $cloudId, spaceKey: $spaceKey)
}
`.trim();

type RemoteObjectSuggestionCountResponse = {
	data?: {
		confluence_remoteObjectSuggestionCount?: number | null;
	};
	errors?: { message?: string }[];
};

export type UseFetchSuggestionCountResult = {
	count: number;
	isLoading: boolean;
	isSuppressed: boolean;
	refetch: () => void;
};

type UseFetchSuggestionCountProps = {
	spaceKey?: string;
	cloudId: string;
};

export const useFetchSuggestionCount = ({
	spaceKey,
	cloudId,
}: UseFetchSuggestionCountProps): UseFetchSuggestionCountResult => {
	const [count, setCount] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [isSuppressed, setIsSuppressed] = useState(false);

	const fetchCount = useCallback(async () => {
		if (!spaceKey) {
			setCount(0);
			setIsLoading(false);
			return;
		}

		setIsLoading(true);

		try {
			if (expVal('confluence_autonomous_space_curation_gql', 'isEnabled', false)) {
				const response = await cfetch(
					`/gateway/api/graphql?q=${REMOTE_OBJECT_SUGGESTION_COUNT_OPERATION}`,
					{
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
						},
						body: JSON.stringify({
							query: REMOTE_OBJECT_SUGGESTION_COUNT_QUERY,
							variables: { cloudId, spaceKey },
							operationName: REMOTE_OBJECT_SUGGESTION_COUNT_OPERATION,
						}),
					},
				);

				const json = (await response.json()) as RemoteObjectSuggestionCountResponse;

				if (json.errors?.length) {
					setIsSuppressed(false);
					return;
				}

				const result = json.data?.confluence_remoteObjectSuggestionCount;
				if (result == null) {
					setIsSuppressed(true);
				} else {
					setCount(result);
					setIsSuppressed(false);
				}
			} else {
				const response = await cfetch(
					`/wiki/rest/internal/1.0/spaces/${spaceKey}/remote-objects/suggestions/count`,
					{ method: 'GET' },
				);

				const text = await response.text();
				setCount(parseInt(text, 10) || 0);
				setIsSuppressed(false);
			}
		} catch (error) {
			if (BadStatusError.isBadStatusError(error) && error.response.status === 404) {
				setIsSuppressed(true);
			}
			// silently fail on other errors
		} finally {
			setIsLoading(false);
		}
	}, [spaceKey, cloudId]);

	useEffect(() => {
		// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
		void fetchCount();
	}, [fetchCount]);

	return { count, isLoading, isSuppressed, refetch: fetchCount };
};
